// @ts-check
// Etapa 3 (doc 14, 9.2 "validateData"): resumo, linhas com erro destacadas por célula, escolha do que fazer com
// registros já existentes e os critérios de comparação do objeto.
import { html } from 'htm/preact';
import { useMemo, useState } from 'preact/hooks';
import { Checkbox } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { issueMessage, targetLabel } from './import-model.js';

const PREVIEW_ROWS = 200;

/**
 * @typedef {import('./import-model.js').ImportTarget} ImportTarget
 * @typedef {import('./import-model.js').BuiltImportRow} BuiltImportRow
 * @param {{ mode: import('./import-model.js').ImportMode, objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata,
 *   dataRows: string[][], firstRowNumber: number, mapping: Array<string | null>, targets: ImportTarget[], builtRows: BuiltImportRow[],
 *   duplicateStrategy: string, onDuplicateStrategyChange: (strategy: string) => void,
 *   criteria: Array<{ key: string, parts: Array<{ fieldName: string, subFieldName: string | null }> }>,
 *   criteriaKeys: string[], onCriteriaKeysChange: (keys: string[]) => void }} props
 */
export function ReviewStep({
  mode, objectMetadata, dataRows, firstRowNumber, mapping, targets, builtRows, duplicateStrategy, onDuplicateStrategyChange, criteria, criteriaKeys, onCriteriaKeysChange,
}) {
  const [onlyIssues, setOnlyIssues] = useState(false);
  const targetsByKey = useMemo(() => new Map(targets.map((target) => [target.key, target])), [targets]);
  const columns = mapping.map((targetKey, columnIndex) => ({ columnIndex, target: targetKey ? targetsByKey.get(targetKey) : undefined }))
    .filter((column) => column.target !== undefined);
  const errorCount = builtRows.filter((row) => row.errors.length > 0).length;
  const warningCount = builtRows.filter((row) => row.errors.length === 0 && row.warnings.length > 0).length;
  const duplicateCount = builtRows.filter((row) => row.warnings.some((warning) => warning.code === 'duplicateOfRow')).length;
  const visible = (onlyIssues ? builtRows.filter((row) => row.errors.length > 0 || row.warnings.length > 0) : builtRows).slice(0, PREVIEW_ROWS);
  const strategies = mode === 'family' ? ['SKIP', 'UPDATE'] : ['SKIP', 'UPDATE', 'CREATE'];
  const fieldsByName = new Map(objectMetadata.fields.map((field) => [field.name, field]));
  const criterionLabel = (/** @type {{ parts: Array<{ fieldName: string, subFieldName: string | null }> }} */ criterion) => criterion.parts
    .map((part) => {
      const label = translateMetadataLabel(fieldsByName.get(part.fieldName)?.label ?? part.fieldName);
      return part.subFieldName && ['firstName', 'lastName'].includes(part.subFieldName) ? t(`spreadsheetImport.subFields.${part.subFieldName}`) : label;
    })
    .join(' + ');

  return html`<div class="spreadsheet-import-review">
    <h3 class="spreadsheet-import-step-title">${t('spreadsheetImport.validate.title')}</h3>
    <div class="spreadsheet-import-chips">
      <span class="spreadsheet-import-chip">${t('spreadsheetImport.validate.rows', { count: builtRows.length })}</span>
      <span class="spreadsheet-import-chip" data-variant=${errorCount > 0 ? 'error' : 'success'}>${t('spreadsheetImport.validate.withErrors', { count: errorCount })}</span>
      ${warningCount > 0 ? html`<span class="spreadsheet-import-chip" data-variant="warning">${t('spreadsheetImport.validate.withWarnings', { count: warningCount })}</span>` : null}
      ${duplicateCount > 0 ? html`<span class="spreadsheet-import-chip" data-variant="warning">${t('spreadsheetImport.validate.duplicatesInFile', { count: duplicateCount })}</span>` : null}
    </div>

    <div class="spreadsheet-import-options">
      <fieldset class="spreadsheet-import-fieldset">
        <legend class="spreadsheet-import-section-label">${t('spreadsheetImport.validate.duplicateTitle')}</legend>
        ${strategies.map((strategy) => html`<label class="spreadsheet-import-radio" key=${strategy}>
          <input type="radio" name="spreadsheet-import-strategy" value=${strategy} checked=${duplicateStrategy === strategy}
            onChange=${() => onDuplicateStrategyChange(strategy)} />
          <span>${t(mode === 'family' ? `spreadsheetImport.validate.familyStrategy.${strategy}` : `spreadsheetImport.validate.strategy.${strategy}`)}</span>
        </label>`)}
        ${mode === 'family' ? html`<p class="spreadsheet-import-hint">${t('spreadsheetImport.validate.familyRules')}</p>` : null}
      </fieldset>
      ${mode === 'object'
        ? html`<fieldset class="spreadsheet-import-fieldset" disabled=${duplicateStrategy === 'CREATE'}>
            <legend class="spreadsheet-import-section-label">${t('spreadsheetImport.validate.duplicateCompare')}</legend>
            ${criteria.length === 0 ? html`<p class="spreadsheet-import-hint">${t('spreadsheetImport.validate.noCriteria')}</p>` : null}
            ${criteria.map((criterion) => html`<label class="spreadsheet-import-check" key=${criterion.key}>
              <${Checkbox} checked=${criteriaKeys.includes(criterion.key)} disabled=${duplicateStrategy === 'CREATE'}
                onChange=${(/** @type {boolean} */ checked) => onCriteriaKeysChange(checked
                  ? [...criteriaKeys, criterion.key]
                  : criteriaKeys.filter((key) => key !== criterion.key))} />
              <span>${criterionLabel(criterion)}</span>
            </label>`)}
          </fieldset>`
        : null}
    </div>

    <div class="spreadsheet-import-review-toolbar">
      <label class="spreadsheet-import-check">
        <${Checkbox} checked=${onlyIssues} onChange=${(/** @type {boolean} */ value) => setOnlyIssues(value)} />
        <span>${t('spreadsheetImport.validate.onlyErrors')}</span>
      </label>
      ${errorCount > 0 ? html`<span class="spreadsheet-import-hint"><${Icon} name="IconInfoCircle" size=${14} /> ${t('spreadsheetImport.validate.errorsIgnored')}</span>` : null}
    </div>

    ${onlyIssues && visible.length === 0
      ? html`<p class="spreadsheet-import-empty">${t('spreadsheetImport.validate.noErrors')}</p>`
      : html`<div class="spreadsheet-import-preview">
          <table>
            <thead>
              <tr>
                <th>${t('spreadsheetImport.validate.row')}</th>
                ${columns.map(({ target, columnIndex }) => html`<th key=${columnIndex} title=${targetLabel(/** @type {ImportTarget} */ (target))}>
                  ${targetLabel(/** @type {ImportTarget} */ (target))}
                </th>`)}
              </tr>
            </thead>
            <tbody>
              ${visible.map((row) => {
                const cells = dataRows[row.rowNumber - firstRowNumber] ?? [];
                const rowIssues = row.warnings.filter((warning) => warning.columnIndex === null);
                return html`<tr key=${row.rowNumber} data-status=${row.errors.length > 0 ? 'error' : row.warnings.length > 0 ? 'warning' : undefined}>
                  <td class="spreadsheet-import-row-number" title=${rowIssues.map(issueMessage).join('\n')}>
                    ${row.rowNumber}
                    ${rowIssues.length > 0 ? html`<${Icon} name="IconAlertTriangle" size=${12} />` : null}
                  </td>
                  ${columns.map(({ columnIndex }) => {
                    const issue = row.errors.find((error) => error.columnIndex === columnIndex);
                    return html`<td key=${columnIndex} data-error=${issue ? 'true' : undefined} title=${issue ? issueMessage(issue) : cells[columnIndex] ?? ''}>
                      ${cells[columnIndex] ?? ''}
                    </td>`;
                  })}
                </tr>`;
              })}
            </tbody>
          </table>
        </div>`}
    ${builtRows.length > visible.length && !onlyIssues
      ? html`<p class="spreadsheet-import-hint">${t('spreadsheetImport.validate.previewLimit', { shown: visible.length, total: builtRows.length })}</p>`
      : null}
  </div>`;
}
